import React, { FC, useCallback } from 'react';
import {
  CircularProgress,
  Table,
  TableBody,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';
import { IoEarthSharp as UrlIcon } from '@react-icons/all-files/io5/IoEarthSharp';
import TableCell from '../../../../../../components/TableCell';
import clsx from 'clsx';
import moment from 'moment';
import { Skeleton } from '@material-ui/lab';
import { createUseStyles } from '../../../../../../theming';
import { Link } from 'react-router-dom';
import Ticket, { TicketClosedReason } from '../../../../../../interfaces/api/Ticket';
import Employee from '../../../../../../interfaces/api/Employee';
import data_criticity from '../../../../../../data/criticity';
import data_priority from '../../../../../../data/priority';
import Site from '../../../../../../interfaces/api/Site';
import data_ticket_state from '../../../../../../data/ticket-state';

interface Props {
  className?: string;
  onChangeLimit: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangePage: (event: unknown, newPage: number) => void;
  onRequestSort: (orderBy: string) => void;
  closedReasons: TicketClosedReason[];
  tickets: Ticket[];
  loading: boolean;
  error?: string;
  employees: Employee[];
  employeesLoading: boolean;
  employeesError?: string;
  sites: Site[];
  sitesLoading: boolean;
  sitesError?: string;
  total: number;
  limit: number;
  page: number;
  orderBy?: string;
  order?: 'asc' | 'desc';
}

const useStyles = createUseStyles({
  root: {},
  tableContainer: {
    overflowX: 'auto',
    width: '100%',
    paddingLeft: 5,
  },
  danger: {
    color: 'red',
    fontWeight: 'bold',
  },
  iconButton: {
    cursor: 'pointer',
  },
  error: {
    color: '#f44336 !important',
  },
  footer: {
    background: '#fcfbfc',
    borderRadius: 3,
    marginTop: 10,
    padding: 15,
  },
});

const headCells = [
  {
    id: 'subject',
    numeric: false,
    label: 'Sujet',
    sortable: false,
  },
  {
    id: 'siteId',
    numeric: false,
    label: 'Client',
    sortable: false,
  },
  {
    id: 'supportEmployeeId',
    numeric: false,
    label: 'Employé en charge',
    sortable: false,
  },
  {
    id: 'criticity',
    numeric: false,
    label: 'Criticité',
    sortable: true,
  },
  {
    id: 'priority',
    numeric: false,
    label: 'Priorité',
    sortable: true,
  },
  {
    id: 'createdAt',
    numeric: false,
    label: 'Date création',
    sortable: true,
  },
  {
    id: 'dateLastUpdate',
    numeric: false,
    label: 'Date dernière MAJ',
    sortable: true,
  },
];

const TicketTable: FC<Props> = ({
  tickets,
  closedReasons,
  loading,
  error,
  employees,
  employeesLoading,
  employeesError,
  sites,
  sitesLoading,
  sitesError,
  onChangeLimit,
  onChangePage,
  onRequestSort,
  order,
  orderBy,
  total,
  limit,
  page,
  className,
  ...props
}) => {
  const classes = useStyles();

  const getSite = useCallback((id?: string) => sites.find((site) => site.id === id), [sites]);

  const openLink = (link?: string) => {
    link = link?.match(/^http[s]*:\/\//) ? link : 'http://' + link;
    const win = window.open(link, '_blank');
    win?.focus();
  };

  const getRowColor = (stateId: number) => {
    return data_ticket_state[stateId]?.color;
  };

  return (
    <div className={clsx(className, classes.root)} {...props}>
      <div className={classes.tableContainer}>
        <Table style={{ minWidth: 815 }}>
          <TableHead>
            <TableRow>
              {headCells.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  align={headCell.numeric ? 'right' : 'left'}
                  sortDirection={orderBy === headCell.id ? order : false}
                >
                  {headCell.sortable ? (
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() => onRequestSort(headCell.id)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  ) : (
                    headCell.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={headCells.length} align='center'>
                  <CircularProgress size={15} />
                </TableCell>
              </TableRow>
            )}

            {!error && !loading && tickets.length <= 0 && (
              <TableRow>
                <TableCell colSpan={headCells.length}>Aucun ticket ne correspond à votre recherche</TableCell>
              </TableRow>
            )}

            {error && (
              <TableRow>
                <TableCell colSpan={headCells.length} className={classes.error}>
                  {error}
                </TableCell>
              </TableRow>
            )}

            {tickets.map((ticket) => {
              const supportEmployee = employees.find((employee) => employee.id === ticket.supportEmployeeId);
              const isNotUpdatedToday = moment(ticket.dateLastUpdate).isBefore(moment().startOf('day'));
              const rowColor = getRowColor(ticket.stateId);

              return (
                <TableRow style={{ borderLeft: `5px solid ${rowColor}` }} key={ticket.id}>
                  <TableCell>
                    {ticket.urlSupplierTicket && (
                      <span title={'Ouvrir ticket fournisseur: ' + ticket.urlSupplierTicket} style={{ marginRight: 5 }}>
                        <UrlIcon
                          size={15}
                          className={classes.iconButton}
                          onClick={() => openLink(ticket.urlSupplierTicket)}
                        />
                      </span>
                    )}
                    <Link to={`/dashboard/tickets/${ticket.id}`}>{ticket.subject}</Link>
                  </TableCell>

                  <TableCell>
                    <SiteLink site={getSite(ticket.siteId)} />
                  </TableCell>

                  <TableCell>
                    {!ticket.supportEmployeeId ? (
                      <span>Non-renseigné</span>
                    ) : supportEmployee ? (
                      <Link to={`/dashboard/user/${supportEmployee.legacyUserId}`}>
                        <span>{`${supportEmployee.firstname} ${supportEmployee.lastname}`}</span>
                      </Link>
                    ) : employeesLoading ? (
                      <Skeleton />
                    ) : (
                      <span>{employeesError}</span>
                    )}
                  </TableCell>

                  <TableCell>{data_criticity[ticket.criticity].fr}</TableCell>

                  <TableCell>{data_priority[ticket.priority].fr}</TableCell>

                  <TableCell>{moment(ticket.createdAt).format('DD-MM-YY')}</TableCell>

                  <TableCell>
                    {ticket.dateLastUpdate ? (
                      <span className={clsx({ [classes.danger]: isNotUpdatedToday })}>
                        {moment(ticket.dateLastUpdate).format('DD-MM-YY à HH:mm')}
                      </span>
                    ) : (
                      '----'
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <TablePagination
        className={classes.footer}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
        labelRowsPerPage='Tickets par page: '
        component='div'
        count={total}
        rowsPerPage={limit}
        page={page}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeLimit}
        SelectProps={{ native: true }}
      />
    </div>
  );
};

const SiteLink: FC<{ site?: Site }> = ({ site }) => {
  if (!site) {
    return <>---</>;
  }

  return <Link to={`/dashboard/customer/${site.legacyCustomerId}`}>{site.socialReason || '---'}</Link>;
};

export default TicketTable;
